import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { flatRatePlanNames } from "@common/flatRateBilling";
import {
    OrganizationAdminQuantitiesExplainEntity,
    OrganizationAdminQuantitiesExplainWorkspace,
} from "@common/schemas/organization-admin.schema";
import {
    OrganizationBillingClientsColumn,
    OrganizationBillingClientsView,
} from "@common/types/views/organizationBillingClientsView";
import { ViewDensity, ViewType } from "@common/types/views/views";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewConfig } from "@lib/views";

export interface BillingClientsViewRow {
    id: string;
    name: string;
    plan: string;
    status: string;
    extraEntities: string;
    why: string;
    children?: BillingClientsViewRow[];
}

const PLAN_NAME_BY_PLAN: Record<string, string> = flatRatePlanNames;

const LICENSE_STATUS_LABEL: Record<
    OrganizationAdminQuantitiesExplainWorkspace["licenseStatus"],
    string
> = {
    billed: "Billed",
    complimentary: "Complimentary",
    custom: "Custom price",
    not_billed: "Not billed",
    excluded: "Not counted",
};

const ENTITY_ROLE_LABEL: Record<
    OrganizationAdminQuantitiesExplainEntity["role"],
    string
> = {
    paid_additional: "Billed add-on",
    included_in_license: "Included in seat",
    below_threshold: "Below threshold",
    excluded: "Excluded",
};

const formatPlan = (plan: string | null): string => {
    if (!plan) {
        return "-";
    }

    return PLAN_NAME_BY_PLAN[plan] ?? plan;
};

const canExpandWorkspace = (
    workspace: OrganizationAdminQuantitiesExplainWorkspace,
): boolean =>
    workspace.additionalEntityCount > 0 || workspace.entities.length > 1;

const buildEntityRow = ({
    workspaceId,
    entity,
}: {
    workspaceId: string;
    entity: OrganizationAdminQuantitiesExplainEntity;
}): BillingClientsViewRow => ({
    id: `${workspaceId}:${entity.entityId}`,
    name: entity.name,
    plan: "-",
    status: ENTITY_ROLE_LABEL[entity.role],
    extraEntities: entity.role === "paid_additional" ? "1" : "-",
    why: "-",
});

export const buildBillingClientsRows = (
    workspaces: OrganizationAdminQuantitiesExplainWorkspace[],
): BillingClientsViewRow[] => {
    const statusOrder: Record<
        OrganizationAdminQuantitiesExplainWorkspace["licenseStatus"],
        number
    > = {
        billed: 0,
        custom: 1,
        complimentary: 2,
        not_billed: 3,
        excluded: 4,
    };

    return [...workspaces]
        .sort((left, right) => {
            const byStatus =
                statusOrder[left.licenseStatus] -
                statusOrder[right.licenseStatus];
            if (byStatus !== 0) {
                return byStatus;
            }
            return left.workspaceName.localeCompare(right.workspaceName);
        })
        .map((workspace) => {
            const row: BillingClientsViewRow = {
                id: workspace.workspaceId,
                name: workspace.workspaceName,
                plan: formatPlan(workspace.plan),
                status: LICENSE_STATUS_LABEL[workspace.licenseStatus],
                extraEntities: String(workspace.additionalEntityCount),
                why: workspace.reason,
            };

            if (canExpandWorkspace(workspace)) {
                row.children = workspace.entities.map((entity) =>
                    buildEntityRow({
                        workspaceId: workspace.workspaceId,
                        entity,
                    }),
                );
            }

            return row;
        });
};

const ClientNameCell = memo(function ClientNameCell({
    row,
}: CellContext<ViewTableFeatures, BillingClientsViewRow, unknown>) {
    // Expand column: ExpandCollapseCell already provides ViewTableCell chrome.
    return <span>{row.original.name}</span>;
});

const TextCell = memo(function TextCell({
    row,
}: CellContext<ViewTableFeatures, BillingClientsViewRow, unknown>) {
    return <ViewTableCell>{row.original.plan}</ViewTableCell>;
});

const StatusCell = memo(function StatusCell({
    row,
}: CellContext<ViewTableFeatures, BillingClientsViewRow, unknown>) {
    return <ViewTableCell>{row.original.status}</ViewTableCell>;
});

const ExtraEntitiesCell = memo(function ExtraEntitiesCell({
    row,
}: CellContext<ViewTableFeatures, BillingClientsViewRow, unknown>) {
    return <ViewTableCell>{row.original.extraEntities}</ViewTableCell>;
});

const WhyCell = memo(function WhyCell({
    row,
}: CellContext<ViewTableFeatures, BillingClientsViewRow, unknown>) {
    return <ViewTableCell>{row.original.why}</ViewTableCell>;
});

export const billingClientsViewConfig: ViewConfig<
    BillingClientsViewRow,
    OrganizationBillingClientsColumn,
    never
> = {
    columns: {
        [OrganizationBillingClientsColumn.CLIENT]: {
            accessorKey: "name",
            name: "Client",
            header: "Client",
            immutable: true,
            cell: ClientNameCell,
            minSize: 180,
            maxSize: 480,
        },
        [OrganizationBillingClientsColumn.PLAN]: {
            accessorKey: "plan",
            name: "Plan",
            header: "Plan",
            cell: TextCell,
            minSize: 100,
            maxSize: 180,
        },
        [OrganizationBillingClientsColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            cell: StatusCell,
            minSize: 120,
            maxSize: 200,
        },
        [OrganizationBillingClientsColumn.EXTRA_ENTITIES]: {
            accessorKey: "extraEntities",
            name: "Additional entities",
            header: "Additional entities",
            align: "right",
            cell: ExtraEntitiesCell,
            minSize: 110,
            maxSize: 160,
        },
        [OrganizationBillingClientsColumn.WHY]: {
            accessorKey: "explanation",
            name: "Explanation",
            header: "Explanation",
            cell: WhyCell,
            minSize: 180,
            maxSize: 560,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: OrganizationBillingClientsColumn.CLIENT,
        childrenKey: "children",
    },
    density: ViewDensity.SM,
};

export const createBillingClientsView = (): OrganizationBillingClientsView => ({
    type: ViewType.ORGANIZATION_BILLING_CLIENTS,
    columns: [
        { id: OrganizationBillingClientsColumn.CLIENT },
        { id: OrganizationBillingClientsColumn.PLAN },
        { id: OrganizationBillingClientsColumn.STATUS },
        { id: OrganizationBillingClientsColumn.EXTRA_ENTITIES },
        { id: OrganizationBillingClientsColumn.WHY },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.SM,
    },
});
